import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { AbcCore } from '@barthauer/abc/core';
import {
  ObjectTypeInfoColumn,
  ObjectTypeInfoColumnViewModel,
  ObjectTypeInfoViewModel,
  QueryColumnSortOrder,
} from '@barthauer/abc/core/data';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { AbcDropDownBoxComponent, _AbcTreeNode } from '@barthauer/abc/ui-components';
import { TextEditorButton } from 'devextreme/common';
import { ValueChangedEvent as TextBoxValueChangedEvent } from 'devextreme/ui/text_box';
import { ValidationType } from 'libs/abc/ui-components/src/lib/core/src/abc-grid/abc-grid';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AbcQueriesEditorService } from '../../../abc-queries-editor.service';
import { QueriesTreeService } from '../../../services/queries-tree.service';
import {
  Column,
  ColumnProperties,
  ColumnPropertiesTranslation,
  ColumnSpacing,
  Property,
} from '../../../viewModels/abc-queries-editor.viewModel';

@Component({
  selector: 'abc-opath-queries-editor-column-properties',
  templateUrl: './column-properties.component.html',
  styleUrls: ['./column-properties.component.scss'],
})
export class ColumnPropertiesComponent implements OnInit, AfterViewInit {
  @ViewChild('expressionDropDownBox') expressionDropDownBox: AbcDropDownBoxComponent;
  @ViewChild('sortingDropDownBox') sortingDropDownBox: AbcDropDownBoxComponent;
  @Input() public set selectedNode(node: _AbcTreeNode<Property>) {
    this.column = node;
    this.columnBackup = this.cloneNode(node);
    this.createSiblingColumns(node);
  }
  @Input() refreshTree: () => void;
  public siblingColumns: Column[];
  public column: _AbcTreeNode<Property>;
  public currentObjectTypeColumns: ObjectTypeInfoColumn[];
  public sortingDropDownDataSource: { caption: string; value: QueryColumnSortOrder }[] = [
    { caption: 'None', value: QueryColumnSortOrder.None },
    { caption: 'Ascending', value: QueryColumnSortOrder.Ascending },
    { caption: 'Descending', value: QueryColumnSortOrder.Descending },
  ];
  public spacingDropDownDataSource: { value: ColumnSpacing }[] = [
    { value: ColumnSpacing.IGNOR_BLANK_PROPERTIES },
    { value: ColumnSpacing.CONSIDER_ELEMENT_PROPERRTIES },
    { value: ColumnSpacing.IGNOR_BLANK },
  ];
  public translation: ColumnPropertiesTranslation;
  public resAsync: Promise<ResourceSet>;

  protected expressionDropDownBoxButtons: (string | TextEditorButton)[] = [
    {
      location: 'after',
      name: 'drop',
      options: {
        icon: 'spindown',
        stylingMode: 'text',
        onClick: () =>
          this.expressionDropDownBox.opened
            ? this.expressionDropDownBox.instance.close()
            : this.expressionDropDownBox.instance.open(),
      },
    },
    {
      location: 'after',
      name: 'open-expression',
      options: { icon: 'more', stylingMode: 'text', onClick: () => this.openExpressionEditor() },
    },
  ];

  private columnBackup: _AbcTreeNode<Property>;
  private $unsubscribe: Subject<void> = new Subject();
  constructor(
    private queiesTreeService: QueriesTreeService,
    private abcQueriesEditorService: AbcQueriesEditorService,
    private resourcesService: ResourceManagerService,
    private abcCore: AbcCore
  ) {
    this.resAsync = this.resourcesService.getResourceSet('abc-opath-queries-editor');
  }

  async ngOnInit(): Promise<void> {
    const res = await this.resAsync;

    this.translation = {
      title: res.get('columProperties-title'),
      expression: res.get('columProperties-expression'),
      alias: res.get('columProperties-alias'),
      visible: res.get('columProperties-visible'),
      formatExpression: res.get('columProperties-formatExpression'),
      neutralCulture: res.get('columProperties-neutralCulture'),
      sorting: res.get('columProperties-sorting'),
      emptyFields: res.get('columProperties-emptyFields'),
      filter: res.get('columProperties-filter'),
    };
  }

  ngAfterViewInit(): void {
    this.expressionDropDownBox.setupForArrayData(this.siblingColumns, 'expression', 'name', [
      { dataField: 'expression' },
    ]);
    this.sortingDropDownBox.setupForArrayData(this.sortingDropDownDataSource, 'value', 'caption', [
      { dataField: 'caption' },
    ]);
  }

  ngOnDestroy(): void {
    this.$unsubscribe.next();
    this.$unsubscribe.complete();
  }

  public async openExpressionEditor(): Promise<void> {
    await this.queiesTreeService.openOpathExpression(
      ValidationType.Column,
      this.column,
      this.abcQueriesEditorService.queryDocument?.ParameterDefinitions || [],
      null,
      this.translation.expression
    );
  }

  public refresh(): void {
    if (this.notEqual(this.column, this.columnBackup)) {
      this.refreshTree();
      this.columnBackup = this.cloneNode(this.column);
      const expression = this.column.data.expression;
      this.column.caption = expression;
      this.column.data.alias = expression;
      this.column.data.name = expression;
    }
  }

  public changeAlias(event: TextBoxValueChangedEvent): void {
    event.event &&
      ((this.column.caption = event.value?.trim() !== '' ? event.value : this.column.data.name), this.refreshTree());
  }

  private cloneNode(node: _AbcTreeNode<Property>): _AbcTreeNode<Property> {
    const backup: _AbcTreeNode<Property> = new _AbcTreeNode<Property>();
    backup.caption = node.caption;

    backup.data = new Property();
    backup.data.alias = node.data.alias;
    backup.data.expression = node.data.expression;

    backup.data.columnProperties = new ColumnProperties({
      culture: node.data.columnProperties.culture,
      filter: node.data.columnProperties.filter,
      formatExpression: node.data.columnProperties.formatExpression,
      sorting: node.data.columnProperties.sorting,
      spacing: node.data.columnProperties.spacing,
      visible: node.data.columnProperties.visible,
    });

    return backup;
  }

  private notEqual(node1: _AbcTreeNode<Property>, node2: _AbcTreeNode<Property>): boolean {
    return (
      node1.caption !== node2.caption ||
      node1.data.alias !== node2.data.alias ||
      node1.data.expression !== node2.data.expression ||
      node1.data.columnProperties.culture !== node2.data.columnProperties.culture ||
      node1.data.columnProperties.filter !== node2.data.columnProperties.filter ||
      node1.data.columnProperties.formatExpression !== node2.data.columnProperties.formatExpression ||
      node1.data.columnProperties.sorting !== node2.data.columnProperties.sorting ||
      node1.data.columnProperties.spacing !== node2.data.columnProperties.spacing ||
      node1.data.columnProperties.visible !== node2.data.columnProperties.visible
    );
  }

  private createSiblingColumns(node: _AbcTreeNode<Property>): void {
    const createColumns = (objectTypeInfo: ObjectTypeInfoViewModel): Column[] => {
      const columns: Column[] = [];
      objectTypeInfo.columnsByName.forEach(async (columnInfo: ObjectTypeInfoColumnViewModel) => {
        columns.push({ name: columnInfo.displayText, expression: columnInfo.name });
      });

      return columns;
    };

    this.abcCore.objectSpace
      .getObjectType((node.parentNode as _AbcTreeNode<Property>).data.name)
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe((objectTypeInfoViewModel) => {
        if (objectTypeInfoViewModel) {
          this.siblingColumns = createColumns(objectTypeInfoViewModel);
        }
      });
  }
}
